/**
 * Entry points to the in-app book (ebook-plan D14): the Početna card and the Profil → Alati
 * tile. Both render only when `GET /api/book` says `visible` for this account, so the book stays
 * invisible to everyone else until the server flag changes. The tile has its own state and never
 * the subscription lock text (critic CR-22): the book is not part of any plan (D3).
 */
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight } from 'lucide-react';
import { useBookState } from '@/hooks/useBook';
import { useLanguage } from '@/store';
import { ROUTES } from '@/config/routes';
import { cn } from '@/utils/cn';
import { fillTemplate } from '@/utils/template';
import { getBookPurchaseTranslation, getBookTranslation, type BookTranslationKey } from '@/utils/translations';
import { BOOK_COVER } from './bookHelpers';
import { BOOK_BUY_PATH, purchaseOffer } from './purchaseHelpers';

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

function useVisibleBook() {
  const { data } = useBookState();
  return data?.visibility === 'visible' ? data : null;
}

/** Početna: cover, title, one line on what it is, and the language note. */
export function BookHomeCard() {
  const language = useLanguage();
  const book = useVisibleBook();
  if (!book) return null;
  const t = (key: BookTranslationKey) => getBookTranslation(key, language);
  return (
    <Link
      to={ROUTES.book}
      data-testid="book-card"
      className={cn('flex items-center gap-4 rounded-xl border bg-card p-3 text-card-foreground transition-colors hover:bg-accent', FOCUS_RING)}
    >
      <img src={BOOK_COVER.small} alt="" width={60} height={85} className="h-[85px] w-[60px] shrink-0 rounded-md object-cover shadow" />
      <span className="min-w-0 flex-1 space-y-1">
        <span className="block font-semibold">{t('cardTitle')}</span>
        <span className="block text-sm text-muted-foreground">{t('cardText')}</span>
        <span className="block text-xs text-muted-foreground">{t('languageNote')}</span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
    </Link>
  );
}

/** Profil → Alati: a "Knjiga" tile next to Pendulum. */
export function BookToolTile() {
  const language = useLanguage();
  const book = useVisibleBook();
  if (!book) return null;
  const t = (key: BookTranslationKey) => getBookTranslation(key, language);
  return (
    <Link
      to={ROUTES.book}
      data-testid="book-tool-tile"
      className="flex min-h-11 items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent"
    >
      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
        <BookOpen className="h-5 w-5 text-primary" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{t('toolTitle')}</span>
        <span className="block text-sm text-muted-foreground">{t('cardText')}</span>
        <span className="mt-1 block text-xs text-muted-foreground">{t('languageNote')}</span>
      </span>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
    </Link>
  );
}

/**
 * D20: after a plan purchase (/success), a separate "Dodaj e-knjigu" card that opens the e-book
 * purchase sheet. Never inside the subscription checkout. Shown only when the server offers the
 * purchase to this account (`purchase.available`: staff only while BOOK_PURCHASE=staff).
 */
export function BookUpsellCard() {
  const language = useLanguage();
  const book = useVisibleBook();
  const offer = book ? purchaseOffer(book) : null;
  if (!offer) return null;
  return (
    <div data-testid="book-upsell" className="flex items-center gap-4 rounded-xl border bg-card p-3 text-left text-card-foreground">
      <img src={BOOK_COVER.small} alt="" width={60} height={85} className="h-[85px] w-[60px] shrink-0 rounded-md object-cover shadow" />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="font-semibold">{getBookPurchaseTranslation('upsellTitle', language)}</p>
        <p className="text-xs text-muted-foreground">{getBookTranslation('languageNote', language)}</p>
        <Link
          to={BOOK_BUY_PATH}
          className={cn('inline-flex min-h-11 items-center justify-center rounded-full border px-4 text-sm font-semibold hover:bg-accent', FOCUS_RING)}
        >
          {fillTemplate(getBookPurchaseTranslation('buyCta', language), { price: offer.price })}
        </Link>
      </div>
    </div>
  );
}
